/**
 * DataTab.jsx — Settings Data tab
 *
 * Features:
 * - Export all app data as JSON file
 * - Import from JSON backup file
 * - Shows last export date
 * - Confirmation before import (destructive)
 */

import { useState, useRef } from 'react';
import { api } from '../../lib/api';
import { useToast } from '../layout/Toast';
import { logAction } from '../../lib/actionLogger';
import { useFocus } from '../../hooks/useFocus';
import FocusHide from '../shared/FocusHide';

const DATA_TABLES = [
  'jobs', 'contacts', 'invoices', 'time_entries', 'expenses',
  'static_expenses', 'legal_docs', 'settings', 'roster',
  'action_log', 'pay_periods', 'pay_period_payments',
];

export default function DataTab() {
  const { toast } = useToast();
  const { isHidden } = useFocus();
  const fileInputRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // ── Export ───────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const backup = { exportDate: new Date().toISOString(), tables: {} };

      for (const table of DATA_TABLES) {
        try {
          const data = await api.get(`/${table.replace(/_/g, '-')}`);
          backup.tables[table] = { rows: data, count: data.length };
        } catch (err) {
          console.error(`Export ${table} failed:`, err.message);
          backup.tables[table] = { error: err.message, rows: [] };
        }
      }

      // Create and download the file
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rfos-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      logAction({ action: 'Export Data', description: 'Full data backup exported as JSON' });
      toast('Data exported successfully');
    } catch (err) {
      toast(`Export failed: ${err.message}`, 'error');
    } finally {
      setExporting(false);
    }
  };

  // ── Import — Parse File ─────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (!parsed.tables) {
          toast('Invalid backup file: missing tables', 'error');
          return;
        }
        setImportData(parsed);
        setShowConfirm(true);
      } catch (err) {
        toast('Invalid JSON file', 'error');
      }
    };
    reader.readAsText(file);
    // Reset so the same file can be selected again
    e.target.value = '';
  };

  // ── Import — Execute ────────────────────────────────────────────────────
  const handleImportConfirm = async () => {
    if (!importData?.tables) return;
    setImporting(true);
    setShowConfirm(false);

    try {
      let imported = 0;
      let skipped = 0;

      for (const table of DATA_TABLES) {
        const tableData = importData.tables[table];
        if (!tableData?.rows?.length) {
          skipped++;
          continue;
        }

        // Upsert: import each row via API
        try {
          const endpoint = `/${table.replace(/_/g, '-')}`;
          for (const row of tableData.rows) {
            await api.post(endpoint, row).catch(() =>
              api.put(`${endpoint}/${row.id}`, row).catch(() => {})
            );
          }
          imported++;
        } catch (err) {
          console.error(`Import ${table} failed:`, err.message);
          skipped++;
        }
      }

      logAction({
        action: 'Import Data',
        description: `Imported ${imported} tables, ${skipped} skipped`,
        details: { exportDate: importData.exportDate },
      });
      toast(`Import complete: ${imported} tables imported, ${skipped} skipped`);
      setImportData(null);
    } catch (err) {
      toast(`Import failed: ${err.message}`, 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-text-primary">Export Data</h3>
        <p className="text-text-dim text-xs">
          Download a full backup of all RFOS data as a JSON file. This includes jobs, contacts,
          invoices, time entries, expenses, settings, and all other tables.
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 bg-accent text-bg rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
          >
            {exporting ? 'Exporting...' : 'Download JSON'}
          </button>
          <FocusHide k="settings.dataCsv">
            <button
              onClick={async () => {
                setExporting(true);
                try {
                  const res = await fetch('/api/system-stats/export?format=csv', { credentials: 'include' });
                  if (!res.ok) throw new Error('Export failed');
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `rfos-backup-${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast('CSV exported');
                } catch (err) {
                  toast(`CSV export failed: ${err.message}`, 'error');
                } finally {
                  setExporting(false);
                }
              }}
              disabled={exporting}
              className="px-4 py-2 bg-surface border border-border text-text-primary rounded text-sm font-medium hover:bg-surface-2 disabled:opacity-50 transition-colors"
            >
              Download CSV
            </button>
          </FocusHide>
        </div>
      </div>

      {/* Divider */}
      {!isHidden('settings.dataCsv') && <div data-focus="settings.dataCsv" className="border-t border-border" />}

      {/* Import Section */}
      {!isHidden('settings.dataCsv') && (
        <div data-focus="settings.dataCsv" className="space-y-3">
          <h3 className="text-sm font-semibold text-text-primary">Import Data</h3>
          <p className="text-text-dim text-xs">
            Restore from a previously exported JSON backup. Existing records with matching IDs
            will be updated; new records will be added. This does not delete existing data.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="hidden"
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="px-4 py-2 bg-surface border border-border text-text-primary rounded text-sm font-medium hover:bg-surface-2 disabled:opacity-50 transition-colors"
          >
            {importing ? 'Importing...' : 'Select Backup File'}
          </button>
        </div>
      )}

      {/* Import Confirmation */}
      {showConfirm && importData && !isHidden('settings.dataCsv') && (
        <div data-focus="settings.dataCsv" className="p-4 bg-red-accent/5 border border-red-accent/30 rounded space-y-3">
          <h4 className="text-sm font-semibold text-red-accent">Confirm Import</h4>
          <p className="text-text-primary text-sm">
            Backup from <strong>{new Date(importData.exportDate).toLocaleString()}</strong>
          </p>
          <div className="text-text-dim text-xs space-y-1">
            {DATA_TABLES.map((t) => {
              const count = importData.tables[t]?.count || importData.tables[t]?.rows?.length || 0;
              return (
                <div key={t}>
                  {t}: <span className="text-text-primary">{count} records</span>
                </div>
              );
            })}
          </div>
          <p className="text-red-accent text-xs font-medium">
            Records with matching IDs will be overwritten. This cannot be undone.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleImportConfirm}
              className="px-4 py-2 bg-red-accent text-white rounded text-sm font-medium hover:bg-red-accent/80 transition-colors"
            >
              Confirm Import
            </button>
            <button
              onClick={() => { setShowConfirm(false); setImportData(null); }}
              className="px-4 py-2 bg-surface border border-border text-text-primary rounded text-sm hover:bg-surface-2 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tables Reference */}
      <div className="p-3 bg-surface border border-border rounded">
        <h4 className="text-2xs font-semibold text-text-dim uppercase mb-2">Included Tables</h4>
        <div className="grid grid-cols-3 gap-1 text-2xs text-text-dim">
          {DATA_TABLES.map((t) => (
            <span key={t}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
